import type { ColDef, ISetFilterParams } from 'ag-grid-community';
import { isValid } from 'date-fns';

import type { AuditLogItem } from '../api';
import { operationTypeNames, type TransformedLog } from './types';

export const auditLogColumnDefs: ColDef<TransformedLog>[] = [
  {
    field: 'operationTimestamp',
    headerName: 'Date',
    width: 200,
    cellRenderer: 'agGroupCellRenderer',
    valueFormatter(params) {
      return formatDate(params.value);
    },
    // Sort is fixed server-side (timestamp DESC); date filtering is driven by
    // the toolbar's date-range presets rather than per-column filter UI.
    filter: false,
    sortable: false,
  },
  {
    field: 'operationType',
    headerName: 'Operation Type',
    width: 170,
    valueFormatter(params) {
      return (
        operationTypeNames[params.value as keyof typeof operationTypeNames] ||
        params.value
      );
    },
    filter: 'agSetColumnFilter',
    filterParams: {
      values: ['INSERT', 'UPDATE', 'DELETE'] as Array<
        keyof typeof operationTypeNames
      >,
      valueFormatter(params: { value: keyof typeof operationTypeNames }) {
        return operationTypeNames[params.value] || params.value;
      },
      defaultToNothingSelected: true,
      suppressSorting: true,
    } as ISetFilterParams,
    sortable: false,
  },
  {
    field: 'userEmail',
    headerName: 'User',
    width: 220,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      suppressAndOrCondition: true,
      buttons: ['reset', 'apply'],
      closeOnApply: true,
    },
    sortable: false,
  },
  {
    field: 'changedColumnsLabel',
    headerName: 'Changed Fields',
    flex: 1,
    minWidth: 240,
    filter: false,
    sortable: false,
    valueGetter: (p) => p.data?.changedColumnsLabel ?? '',
  },
];

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (!isValid(date)) {
    return value;
  }
  return date.toLocaleString();
}

function transformChangeValue(
  dataType: string | null,
  value: { id: string | null; value: string } | null | undefined,
): { id: string | null; value: string } {
  if (!value) {
    return { id: null, value: '' };
  }
  if (dataType === 'datetime') {
    return {
      id: value.id,
      value: formatDate(value.value),
    };
  }
  return { id: value.id, value: value.value };
}

export function transformAuditLog(item: AuditLogItem): TransformedLog {
  const changes: TransformedLog['changes'] = item.changes.map((change) => {
    return {
      id: change.id,
      column: change.displayName,
      originalValue: transformChangeValue(
        change.dataType,
        change.originalValue,
      ),
      newValue: transformChangeValue(change.dataType, change.newValue),
    };
  });
  const columnNames = changes.map((c) => c.column);
  const changedColumnsLabel =
    columnNames.length === 0
      ? ''
      : columnNames.length <= 3
        ? columnNames.join(', ')
        : `${columnNames.slice(0, 3).join(', ')} (+${columnNames.length - 3} more)`;
  return {
    id: item.id,
    operationTimestamp: item.operationTimestamp,
    operationType: item.operationType,
    userEmail: item.userEmail,
    changes,
    changedColumnsLabel,
  };
}
